import { Routes, Route, Navigate, Link } from 'react-router-dom'
import { MapPinOff, Home } from 'lucide-react'
import { useAuth } from './hooks/useAuth'
import Button from './components/ui/Button'

// Layouts
import MainLayout from './components/layout/MainLayout'
import AuthLayout from './components/layout/AuthLayout'

// Páginas públicas (auth)
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'

// Páginas protegidas
import Dashboard from './pages/dashboard/Dashboard'
import RecipientsList from './pages/recipients/RecipientsList'
import RecipientForm from './pages/recipients/RecipientForm'
import DeliveriesList from './pages/deliveries/DeliveriesList'
import DeliveryForm from './pages/deliveries/DeliveryForm'
import RoutesList from './pages/routes/RoutesList'
import RouteCreate from './pages/routes/RouteCreate'
import RouteView from './pages/routes/RouteView'
import DriversList from './pages/drivers/DriversList'
import DriverForm from './pages/drivers/DriverForm'
import FeedbackList from './pages/feedback/FeedbackList'

// Página pública do link do motorista (sem login)
import DriverLinkView from './pages/driverLink/DriverLinkView'

// Página pública de confirmação de pino pelo destinatário (sem login)
import PinConfirmView from './pages/pinConfirm/PinConfirmView'

// Página pública de preenchimento de endereço pelo destinatário (sem login)
import AddressFillView from './pages/addressRequest/AddressFillView'

// Página pública de rastreio pelo destinatário final (sem login)
import TrackingView from './pages/tracking/TrackingView'

// Página pública da política de privacidade (sem login)
import PrivacyView from './pages/privacy/PrivacyView'

// Componente de rota protegida
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return children
}

// Componente de rota pública (redireciona se já logado)
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

function App() {
  return (
    <Routes>
      {/* Rotas públicas (login/cadastro) */}
      <Route element={<PublicRoute><AuthLayout /></PublicRoute>}>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
      </Route>

      {/* Rotas protegidas (logado) */}
      <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        
        {/* Destinatários */}
        <Route path="/recipients" element={<RecipientsList />} />
        <Route path="/recipients/new" element={<RecipientForm />} />
        <Route path="/recipients/:id/edit" element={<RecipientForm />} />
        
        {/* Entregas */}
        <Route path="/deliveries" element={<DeliveriesList />} />
        <Route path="/deliveries/new" element={<DeliveryForm />} />
        
        {/* Rotas de entrega */}
        <Route path="/routes" element={<RoutesList />} />
        <Route path="/routes/new" element={<RouteCreate />} />
        <Route path="/routes/:id" element={<RouteView />} />

        {/* Motoristas */}
        <Route path="/drivers" element={<DriversList />} />
        <Route path="/drivers/new" element={<DriverForm />} />
        <Route path="/drivers/:id/edit" element={<DriverForm />} />

        {/* Feedback */}
        <Route path="/feedback" element={<FeedbackList />} />
      </Route>

      {/* Link público do motorista - FORA de ProtectedRoute e de PublicRoute
          de propósito: não tem conceito de sessão, e pode abrir no celular de
          qualquer pessoa, inclusive o do próprio vendedor logado (que não pode
          ser redirecionado pro dashboard aqui). */}
      <Route path="/driver-link/:token" element={<DriverLinkView />} />

      {/* Link público de confirmação de pino pelo destinatário - mesmo motivo
          do /driver-link acima: sem conceito de sessão, fora de ProtectedRoute
          e de PublicRoute. */}
      <Route path="/confirmar-localizacao/:token" element={<PinConfirmView />} />

      {/* Link público de preenchimento de endereço pelo destinatário - mesmo
          motivo dos links acima: sem conceito de sessão, fora de
          ProtectedRoute e de PublicRoute. */}
      <Route path="/completar-endereco/:token" element={<AddressFillView />} />

      {/* Link público de rastreio pelo destinatário final - mesmo motivo dos
          links acima: sem conceito de sessão, fora de ProtectedRoute e de
          PublicRoute. */}
      <Route path="/rastreio/:code" element={<TrackingView />} />

      {/* Política de privacidade - FORA de ProtectedRoute e de PublicRoute de
          propósito: usuário logado também precisa conseguir abrir, sem ser
          redirecionado pro dashboard. */}
      <Route path="/privacidade" element={<PrivacyView />} />

      {/* Redireciona raiz para dashboard ou login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 */}
      <Route path="*" element={
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <MapPinOff className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-gray-900">404</h1>
            <p className="text-gray-500 mb-4 mt-2">Página não encontrada</p>
            <Link to="/dashboard">
              <Button variant="outline" size="sm">
                <Home className="w-4 h-4" />
                Voltar ao início
              </Button>
            </Link>
          </div>
        </div>
      } />
    </Routes>
  )
}

export default App
